import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#restoreAnchor();
    this.addEventListener('click', this.#handleCardClick, { capture: true });
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagehide', this.#handlePageHide);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagehide', this.#handlePageHide);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Load-more button mode: click-triggered instead of IntersectionObserver.
    // Read off the grid: the next button is gone on the last page.
    if (this.refs.grid?.dataset.loadMore !== undefined) {
      viewMoreNext?.addEventListener('click', this.#handleLoadMoreClick);
      viewMorePrevious?.addEventListener('click', this.#handleLoadPreviousClick);
      this.#updateLoadMoreButton();
      return;
    }

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Load more pushes `?page=N`, so a back navigation that misses the bfcache
   * re-renders the clicked card's page alone (VIS-1085). Put that card back
   * where it was; earlier pages stay one "load previous" click away instead of
   * being fetched up front.
   */
  #restoreAnchor() {
    const anchor = history.state?.paginatedListAnchor;
    if (!anchor) return;

    const card = document.getElementById(anchor.id);
    if (!card) return;

    window.scrollTo({
      top: card.getBoundingClientRect().top + window.scrollY - anchor.top,
      behavior: 'instant',
    });
  }

  /**
   * Once the page is shown the anchor has done its job: drop it so a later
   * reload doesn't jump back. A bfcache restore needs it one last time:
   * Safari skips its own scroll restore for an entry on `manual` and shows
   * the cached page at the top, while Chrome keeps the scroll (the extra
   * scroll is then a no-op).
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!history.state?.paginatedListAnchor) return;
    if (event.persisted) this.#restoreAnchor();
    history.replaceState({ ...history.state, paginatedListAnchor: undefined }, '');
  };

  /**
   * Hands scroll restoration back to the browser when the shopper leaves
   * other than through a card (a reload, another link), so that visit keeps
   * its scroll, but only while the grid holds just the URL's page: that is
   * what Liquid renders next time, so the offset still points at the same
   * products. Not any earlier than `pagehide` either: Chrome still restores
   * after `load` and `pageshow` once the entry is `auto`, over the anchor.
   */
  #handlePageHide = () => {
    if (history.state?.paginatedListAnchor) return;

    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]') ?? [];
    const pages = new Set(Array.from(cards, (card) => card instanceof HTMLElement && card.dataset.page));
    const urlPage = new URL(window.location.href).searchParams.get('page') || '1';
    if (pages.size === 1 && pages.has(urlPage)) history.scrollRestoration = 'auto';
  };

  /**
   * Before leaving for a PDP, points this history entry at the clicked card's
   * page and remembers where the card sat, for #restoreAnchor. The browser's
   * scroll restoration goes off for this entry only: it would put the offset
   * of the longer list onto the single page Liquid renders on the way back.
   * Capture phase and any click in the card, minus the controls
   * product-card.js ignores: it navigates from the card itself too
   * (`window.location.href`), not only from its links.
   *
   * @param {MouseEvent} event
   */
  #handleCardClick = (event) => {
    if (this.refs.grid?.dataset.loadMore === undefined || !(event.target instanceof Element)) return;
    if (event.target.closest('button, input, label, select')) return;

    const card = event.target.closest('[ref="cards[]"]');
    if (!(card instanceof HTMLElement) || !card.dataset.page) return;

    const url = new URL(window.location.href);
    if (card.dataset.page === '1') {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', card.dataset.page);
    }

    history.replaceState(
      { ...history.state, paginatedListAnchor: { id: card.id, top: card.getBoundingClientRect().top } },
      '',
      url.toString()
    );
    history.scrollRestoration = 'manual';
  };

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    // Query directly from DOM to avoid stale refs when called right after grid.append()
    const cards = grid ? Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')) : [];

    if (!cards.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  #handleLoadMoreClick = async () => {
    const { viewMoreNext } = this.refs;
    if (viewMoreNext instanceof HTMLButtonElement) viewMoreNext.disabled = true;
    await this.#renderNextPage();
    this.#updateLoadMoreButton();
  };

  #handleLoadPreviousClick = async () => {
    const { viewMorePrevious } = this.refs;
    if (!(viewMorePrevious instanceof HTMLButtonElement)) return;

    viewMorePrevious.disabled = true;
    const buttonTop = viewMorePrevious.getBoundingClientRect().top;
    await this.#renderPreviousPage();

    // Keep the button where it was clicked, so the page it brought in opens
    // right below it instead of the browser's scroll anchoring deciding.
    window.scrollTo({
      top: viewMorePrevious.getBoundingClientRect().top + window.scrollY - buttonTop,
      behavior: 'instant',
    });
    this.#updateLoadMoreButton();

    // Reaching page 1 hides the focused button: hand focus to the first product.
    if (viewMorePrevious.hidden) this.refs.grid?.querySelector(':scope > [ref="cards[]"] a[href]')?.focus();
  };

  #updateLoadMoreButton() {
    /**
     * @param {Element | undefined} button
     * @param {"previous" | "next"} type
     */
    const update = (button, type) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const page = this.#getPage(type);
      button.hidden = !(page && this.#shouldUsePage(page));
      button.disabled = false;
    };

    update(this.refs.viewMoreNext, 'next');
    update(this.refs.viewMorePrevious, 'previous');
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
